import { c as _c } from "react/compiler-runtime";
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { parseApiAsset, toBigInt, ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { vaultKey } from '@repo/web3/utils';
import { useMemo } from 'react';
import type { CollateralExposure } from '@/components/common/CollateralExposureStack';
import type { VaultListItem } from '@/lib/procedures/vaultList.contract';
import { vaultIncludesDonations } from '@/config/donation-vaults';
import { aggregateVaultListItemWarnings } from '@/helpers/aggregateVaultListWarnings';
import { getParsedValue } from '@/helpers/formatters';
import { computeRewards } from '@/helpers/rewards';
import { isVaultV2RateCapped } from '@/helpers/vaultV2RateCap';
import type { TableVaultAsset, TableVaultRow } from '../config/TableVault.types';
const parseUsdOrNull = (usd: number | null) => usd === null ? null : safeParseNumber(usd);
export const useTableVaultRow = t0 => {
  const $ = _c(6);
  const {
    vaults,
    userVaultPositionKeys
  } = t0;
  let t1;
  bb0: {
    if (!vaults) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[1] !== userVaultPositionKeys || $[2] !== vaults) {
      let t3;
      if ($[4] !== userVaultPositionKeys) {
        t3 = vault => {
          const asset = {
            address: vault.asset.address,
            symbol: vault.asset.symbol,
            name: vault.asset.name ?? undefined,
            decimals: vault.asset.decimals,
            logoURI: vault.asset.logoUri,
            priceUsd: vault.asset.priceUsd,
            yield: vault.asset.yield,
            chain: {
              id: vault.chainId
            }
          };
          const rewards = vault.rewards.map(_temp).filter(_temp2);
          const collateralExposures = vault.exposure.map(exposure => {
            const exposureAsset = exposure.collateralAsset ?? vault.asset;
            return {
              token: parseApiAsset({
                address: exposureAsset.address,
                symbol: exposureAsset.symbol,
                logoURI: exposureAsset.logoUri,
                decimals: exposureAsset.decimals,
                chain: {
                  id: vault.chainId
                }
              }),
              supplyAssetsUsd: parseUsdOrNull(exposure.exposureUsd),
              ...(!exposure.collateralAsset && {
                isIdle: true
              })
            };
          });
          const totalAssets = toBigInt(vault.totalAssets) ?? ZERO;
          const totalAssetsUsd = parseUsdOrNull(vault.totalAssetsUsd);
          const liquidityUsd = parseUsdOrNull(vault.liquidityUsd);
          const performanceFeeBigInt = getParsedValue(vault.performanceFee, safeParseNumber);
          const managementFeeBigInt = getParsedValue(vault.managementFee, safeParseNumber);
          const apy = isDefined(vault.netApyExcludingRewards) ? toBigInt(safeParseNumber(vault.netApyExcludingRewards)) : null;
          const netApy = isDefined(vault.netApy) ? toBigInt(safeParseNumber(vault.netApy)) : null;
          const warnings = aggregateVaultListItemWarnings(vault.warnings);
          return {
            network: {
              chainId: vault.chainId
            },
            vault: {
              address: vault.address,
              name: vault.name,
              asset,
              warnings,
              version: vault.version,
              chainId: vault.chainId,
              hasUserPosition: userVaultPositionKeys.has(vaultKey(vault))
            },
            deposits: {
              amount: totalAssets,
              valueUsd: totalAssetsUsd,
              asset,
              vaultAddress: vault.address,
              vaultVersion: vault.version,
              chainId: vault.chainId
            },
            liquidity: {
              amount: toBigInt(vault.liquidity) ?? ZERO,
              valueUsd: liquidityUsd,
              asset
            },
            curators: vault.curators.map(_temp3),
            exposure: {
              collateralExposures,
              totalAssetsUsd
            },
            performanceFee: vault.performanceFee,
            performanceFeeBigInt,
            managementFee: vault.managementFee,
            managementFeeBigInt,
            rewards,
            asset,
            apy,
            netApy,
            maxApy: vault.maxRate ?? null,
            isRateCapped: isVaultV2RateCapped(vault.apyBreakdown?.apy ?? vault.apy, vault.maxRate),
            includesDonations: vaultIncludesDonations(vault.chainId, vault.address),
            apyBreakdown: vault.version === "2.0" ? vault.apyBreakdown : undefined,
            creationTimestamp: String(vault.creationTimestamp)
          };
        };
        $[4] = userVaultPositionKeys;
        $[5] = t3;
      } else {
        t3 = $[5];
      }
      t2 = vaults.map(t3);
      $[1] = userVaultPositionKeys;
      $[2] = vaults;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = t2;
  }
  return t1;
};
function _temp(reward) {
  return computeRewards({
    rewardToken: {
      address: reward.asset.address,
      symbol: reward.asset.symbol,
      decimals: reward.asset.decimals,
      logoURI: reward.asset.logoUri,
      chain: {
        id: reward.asset.chainId
      }
    },
    apr: reward.supplyApr
  });
}
function _temp2(r) {
  return r.apr && r.apr > ZERO;
}
function _temp3(curator) {
  return {
    id: curator.id,
    name: curator.name,
    image: curator.image,
    state: {
      aum: curator.aumUsd
    }
  };
}